import { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import useFlashNews from "./hooks/useFlashNews";

import "./styles.css";

// Queue/interval entfernt – alle News werden sofort angezeigt

// Relative time formatting
function timeAgo(dateStr) {
  if (!dateStr) return "Just now";
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = now - then;

  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return "Just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

// Extract tags from content
function extractTags(content, source) {
  const tags = [];
  const text = (content || "").toLowerCase();

  if (/\bbitcoin\b|\bbtc\b/i.test(text)) tags.push({ label: "Bitcoin", color: "bg-orange-50 text-orange-700 border-orange-200" });
  if (/\bethereum\b|\beth\b/i.test(text)) tags.push({ label: "Ethereum", color: "bg-purple-50 text-purple-700 border-purple-200" });
  if (/\bsolana\b|\bsol\b/i.test(text)) tags.push({ label: "Solana", color: "bg-green-50 text-green-700 border-green-200" });
  if (/\bxrp\b|\bripple\b/i.test(text)) tags.push({ label: "XRP", color: "bg-blue-50 text-blue-700 border-blue-200" });
  if (/\bregulat\b|\bsec\b|\blegal\b|\blaw\b/i.test(text)) tags.push({ label: "Regulation", color: "bg-red-50 text-red-700 border-red-200" });
  if (/\bnft\b/i.test(text)) tags.push({ label: "NFT", color: "bg-pink-50 text-pink-700 border-pink-200" });
  if (/\bdefi\b/i.test(text)) tags.push({ label: "DeFi", color: "bg-cyan-50 text-cyan-700 border-cyan-200" });
  if (/\bmusk\b|\btesla\b|\bspacex\b/i.test(text)) tags.push({ label: "SpaceX", color: "bg-sky-50 text-sky-700 border-sky-200" });
  if (/\bhack\b|\bexploit\b|\bbreach\b/i.test(text)) tags.push({ label: "Security", color: "bg-red-50 text-red-700 border-red-200" });
  if (/\bairdrop\b/i.test(text)) tags.push({ label: "Airdrop", color: "bg-emerald-50 text-emerald-700 border-emerald-200" });

  if (tags.length === 0 && source) {
    tags.push({ label: source, color: "bg-slate-100 text-slate-600 border-slate-200" });
  }
  if (tags.length === 0) {
    tags.push({ label: "Crypto", color: "bg-cyan-50 text-cyan-700 border-cyan-200" });
  }

  return tags.slice(0, 3);
}

// Helper function to split news into title and description
function splitTitleDesc(text) {
  if (!text) return { title: "", desc: "" };
  let t = String(text).trim();

  // Remove sources section
  const mSources = t.match(/\n\s*Sources:\s*[\s\S]*$/i);
  if (mSources && typeof mSources.index === "number") t = t.slice(0, mSources.index).trim();

  // Try to split at first sentence
  const m = t.match(/^([\s\S]*?[.!?](?:\s*\[\d+\])*(?:[""')\]]*)?)(?:\s+|$)/);
  if (m) return { title: m[1].trim(), desc: t.slice(m[0].length).trim() };

  // Try newline
  const nl = t.indexOf("\n");
  if (nl > 0) return { title: t.slice(0, nl).trim(), desc: t.slice(nl + 1).trim() };

  // Cut at reasonable length
  const cut = Math.min(180, t.length);
  return { title: t.slice(0, cut).trim(), desc: t.slice(cut).trim() };
}

function SkeletonCard() {
  return (
    <div className="news-card-glass news-card-default rounded-xl p-4 md:p-5 animate-pulse">
      <div className="flex gap-4">
        <div className="flex-shrink-0 w-36 h-36 md:w-48 md:h-48 rounded-lg bg-slate-200" />
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex gap-2">
            <div className="h-4 w-16 rounded bg-slate-200" />
            <div className="h-4 w-12 rounded bg-slate-200" />
          </div>
          <div className="h-4 w-full rounded bg-slate-200" />
          <div className="h-4 w-3/4 rounded bg-slate-200" />
          <div className="h-3 w-24 rounded bg-slate-100 mt-1" />
        </div>
      </div>
    </div>
  );
}

export default function MoonupdateLanding() {
  const [showImprint, setShowImprint] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [expandedNews, setExpandedNews] = useState({});

  const { news, error, debug } = useFlashNews();

  const [displayedItems, setDisplayedItems] = useState([]);

  const shownIds = useRef(new Set());
  const pendingQueue = useRef([]);

  // Queue new items (mark IDs immediately so refresh doesn't re-queue them)
  useEffect(() => {
    if (!news || news.length === 0) return;

    const freshItems = news
      .filter(item => !shownIds.current.has(item.id))
      .map(item => ({
        ...item,
        topic: item.source || "Breaking News",
      }));

    if (freshItems.length > 0) {
      freshItems.forEach(item => shownIds.current.add(item.id));
      // oldest first → enters the list first, newest lands at bottom
      freshItems.sort((a, b) => Date.parse(a.insertedAt || 0) - Date.parse(b.insertedAt || 0));
      pendingQueue.current = [...pendingQueue.current, ...freshItems];
    }
  }, [news]);

  // Release one card every 2 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      const next = pendingQueue.current.shift();
      if (!next) return;
      setDisplayedItems(prev => {
        const merged = [next, ...prev];
        // newest at top
        merged.sort((a, b) => Date.parse(b.insertedAt || 0) - Date.parse(a.insertedAt || 0));
        return merged.slice(0, 50);
      });
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const toggleNews = useCallback((id) => {
    setExpandedNews(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  return (
    <div className="relative min-h-screen text-slate-800 overflow-hidden bg-white font-sans">
      {/* White background + Nyan Cat */}
      <div className="vbw-background">
        <div className="absolute inset-0 bg-white" />
        <div className="nyan-cat-container">
          <img src="/nyan-cat.gif" alt="Nyan Cat" className="nyan-cat" />
        </div>
        <div className="nyan-cat-container-2">
          <img src="/nyan-cat.gif" alt="Nyan Cat" className="nyan-cat" style={{ transform: "scaleX(-1)" }} />
        </div>
      </div>

      {/* VBW Header */}
      <header className="relative z-20 border-b border-slate-200 bg-white/95 backdrop-blur-md overflow-hidden">
        <img src="/nyan-cat.gif" alt="🐱" className="nyan-cat-header" style={{ position: "absolute", bottom: "8px", left: "180px" }} />
        <div className="px-6 md:px-10 py-3 flex items-center justify-between">
          <a href="https://viennablockchainweek.org" target="_blank" rel="noopener noreferrer">
            <img src="/vbw-logo.jpg" alt="Vienna Blockchain Week" className="h-20 rounded-lg" />
          </a>
          <div className="flex items-center gap-2 text-xs text-slate-500 font-mono-tech">
            <span className="w-1.5 h-1.5 rounded-full bg-teal-500 animate-pulse inline-block"></span>
            LIVE FEED
          </div>
        </div>
      </header>

      <div className="relative z-10 min-h-screen flex flex-col items-center px-6 md:px-10 py-6">
        {/* Page Header */}
        <div className="w-full mb-6 mt-6">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">
            <span className="gradient-text-vbw">News</span>
          </h1>
        </div>

        {/* Loading State — skeleton screens */}
        {news === undefined && (
          <div className="w-full flex flex-col gap-3">
            {Array.from({ length: 5 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-6 w-full mb-8">
            <p className="text-red-700">{error}</p>
            {debug && (
              <details className="mt-3">
                <summary className="cursor-pointer text-sm text-red-600 hover:text-red-700">Debug Info</summary>
                <pre className="text-xs mt-2 overflow-auto bg-slate-100 p-3 rounded">{JSON.stringify(debug, null, 2)}</pre>
              </details>
            )}
          </div>
        )}

        {/* News Feed */}
        <div className="w-full flex flex-col gap-3">
          {displayedItems.map((item, index) => {
            const isLatest = index === 0;
            const isExpanded = expandedNews[item.id];

            const { title, desc } = splitTitleDesc(item.content);
            const hasLongContent = desc && desc.length > 50;
            const tags = extractTags(item.content, item.source);

            const isToday = (() => {
              if (!item.insertedAt) return false;
              const d = new Date(item.insertedAt);
              const now = new Date();
              return d.getFullYear() === now.getFullYear() &&
                     d.getMonth() === now.getMonth() &&
                     d.getDate() === now.getDate();
            })();

            return (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                className={`news-card-glass rounded-xl transition-all duration-300 ${
                  isLatest
                    ? "news-card-latest"
                    : "news-card-default"
                }`}
              >
                <button
                  onClick={() => hasLongContent && toggleNews(item.id)}
                  className={`w-full p-4 md:p-5 text-left ${hasLongContent ? 'cursor-pointer' : 'cursor-default'}`}
                >
                  <div className="flex gap-4">
                    {/* Thumbnail - left accent */}
                    {item.mediaUrl && (
                      <div className="flex-shrink-0">
                        <img
                          src={item.mediaUrl}
                          alt=""
                          className="w-36 h-36 md:w-48 md:h-48 object-cover rounded-lg"
                          onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                        />
                      </div>
                    )}

                    <div className="flex-1 min-w-0">
                      {/* Tags + NEW badge row */}
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        {tags.map((tag, i) => (
                          <span
                            key={i}
                            className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border ${tag.color}`}
                          >
                            {tag.label}
                          </span>
                        ))}

                        {isToday && (
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-semibold bg-red-50 text-red-600 border border-red-200 ml-auto">
                            <span className="relative flex h-2 w-2">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
                              <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
                            </span>
                            LIVE
                          </span>
                        )}
                      </div>

                      {/* Title - bold, prominent */}
                      <h3 className="text-xl md:text-2xl font-semibold text-slate-900 leading-snug mb-1.5 font-display">
                        {title || item.content}
                      </h3>

                      {/* Preview of description (first line) */}
                      {desc && !isExpanded && (
                        <p className="text-base text-slate-600 leading-relaxed line-clamp-2 mb-2">
                          {desc.slice(0, 120)}{desc.length > 120 ? "..." : ""}
                        </p>
                      )}

                      {/* Metadata row */}
                      <div className="flex items-center gap-3 text-sm text-gray-500">
                        <span className="text-slate-400">{timeAgo(item.insertedAt)}</span>
                        {item.url && (
                          <>
                            <span className="text-gray-600">·</span>
                            <a
                              href={item.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-cyan-600 hover:text-cyan-800 transition-colors font-medium"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Source
                            </a>
                          </>
                        )}
                        {hasLongContent && (
                          <>
                            <span className="text-gray-600">·</span>
                            <span className="text-cyan-600 font-medium">
                              {isExpanded ? 'Less' : 'More'}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </button>

                {/* Expandable content */}
                <AnimatePresence initial={false}>
                  {isExpanded && hasLongContent && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      style={{ overflow: "hidden" }}
                    >
                      <div className="px-4 md:px-5 pb-4 md:pb-5 text-lg text-slate-700 leading-relaxed border-t border-slate-200 pt-3">
                        {desc}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}

          {displayedItems.length === 0 && news && news.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <p className="text-lg font-display">No flash news available at the moment.</p>
              <p className="text-sm mt-2">Check back soon for updates!</p>
            </div>
          )}
        </div>

        {/* App Download */}
        <div className="mt-6">
          <a
            href="https://play.google.com/store/apps/details?id=io.moonboard.twa"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block opacity-80 hover:opacity-100 transition-opacity"
          >
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
              alt="Get it on Google Play"
              className="h-12"
            />
          </a>
        </div>

        {/* Footer */}
        <footer className="mt-12 mb-8 w-full border-t border-slate-200 pt-8">
          {/* Logo + tagline */}
          <div className="flex flex-col items-center mb-6">
            <a href="https://viennablockchainweek.org" target="_blank" rel="noopener noreferrer" className="mb-3">
              <img src="/vbw-logo.jpg" alt="Vienna Blockchain Week" className="h-8 rounded-lg" />
            </a>
            <p className="text-xs text-slate-400">Powered by neob.ai · moonupdate.io</p>
          </div>

          {/* Backlinks */}
          <div className="flex flex-wrap justify-center gap-3 mb-6">
            <a
              href="https://www.youtube.com/@ed_prinz"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 bg-white text-slate-700 text-xs font-medium hover:border-red-300 hover:text-red-600 transition-all shadow-sm"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
              YouTube
            </a>
            <a
              href="https://www.tiktok.com/@ed_prinz"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 bg-white text-slate-700 text-xs font-medium hover:border-slate-400 hover:text-slate-900 transition-all shadow-sm"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.34 6.34 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.76a4.85 4.85 0 0 1-1.01-.07z"/>
              </svg>
              TikTok
            </a>
            <a
              href="https://bitpull.ai/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 bg-white text-slate-700 text-xs font-medium hover:border-electric-cyan hover:text-cyan-700 transition-all shadow-sm"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              bitpull.ai
            </a>
          </div>

          {/* Legal links */}
          <div className="flex flex-wrap justify-center gap-5 text-xs text-slate-400 mb-3">
            <button
              onClick={() => { setShowImprint(!showImprint); setShowPrivacy(false); }}
              className="hover:text-slate-600 transition-colors"
            >
              {showImprint ? "Hide Imprint" : "Imprint"}
            </button>
            <button
              onClick={() => { setShowPrivacy(!showPrivacy); setShowImprint(false); }}
              className="hover:text-slate-600 transition-colors"
            >
              {showPrivacy ? "Hide Privacy" : "Privacy Policy"}
            </button>
            <a href="https://viennablockchainweek.org" target="_blank" rel="noopener noreferrer" className="hover:text-slate-600 transition-colors">
              Vienna Blockchain Week
            </a>
            <a href="https://x.com/_Ed_Prinz" target="_blank" rel="noopener noreferrer" className="hover:text-slate-600 transition-colors">
              X / Twitter
            </a>
          </div>
          <p className="text-center text-xs text-slate-300">© {new Date().getFullYear()} moonupdate.io</p>
        </footer>

        {showImprint && (
          <div className="mt-4 w-full text-left text-slate-600 news-card-glass p-6 rounded-xl text-xs leading-relaxed">
            <p className="font-semibold text-slate-900 mb-3 font-display">Moonlytics.ai / Moonupdate.io</p>
            <p className="mb-3">
              Operated by:<br />
              Ed Prinz, MSc<br />
              District Living · Office 13<br />
              Donau-City-Straße 3<br />
              1220 Vienna · Austria
            </p>
            <p className="mb-3">
              Contact:{" "}
              <a href="mailto:support@moonlytics.ai" className="text-cyan-600 hover:text-cyan-800">
                support@moonlytics.ai
              </a>
            </p>
            <p className="mb-3">
              Business type: Sole proprietorship (Einzelunternehmen)<br />
              Member of the Vienna Chamber of Commerce<br />
              Supervisory authority: Municipal District Office 22 – Vienna (Magistratisches Bezirksamt 1220)<br />
              Legal basis:{" "}
              <a
                href="https://www.ris.bka.gv.at"
                target="_blank"
                rel="noopener noreferrer"
                className="text-cyan-600 hover:text-cyan-800"
              >
                www.ris.bka.gv.at
              </a>
            </p>
            <p className="mb-3">
              Responsible for content according to § 55 Abs. 2 RStV:<br />
              Ed Prinz, MSc – address as above
            </p>
            <div className="mt-4 pt-4 border-t border-slate-200">
              <p className="mb-2"><strong className="text-slate-800">Disclaimer:</strong> The content on this website is for informational purposes only and does not constitute financial advice.</p>
              <p className="mb-2"><strong className="text-slate-800">DYOR:</strong> Always do your own research.</p>
              <p className="mb-2"><strong className="text-slate-800">AI-generated:</strong> All insights are generated by AI – accuracy cannot be guaranteed.</p>
              <p><strong className="text-slate-800">Security:</strong> Admins will never DM you first. Block and report scammers.</p>
            </div>
          </div>
        )}

        {showPrivacy && (
          <div className="mt-4 w-full text-left text-slate-600 news-card-glass p-6 rounded-xl text-xs leading-relaxed">
            <p className="font-semibold text-slate-900 mb-3 font-display">Privacy Policy</p>
            <p className="mb-3">
              At Moonlytics.ai / Moonupdate.io, we take your privacy seriously. This website does not collect, store, analyze, or process any personal data — either directly or indirectly.
            </p>
            <p className="mb-3">
              We do not use cookies, tracking pixels, or any analytics tools that collect user behavior or identifiable information.
            </p>
            <p className="mb-3">
              No user profiles are created, and no user data is shared with third parties.
            </p>
            <p className="mb-3">
              If you contact us via email, your message will be treated confidentially and not stored beyond what is necessary to respond.
            </p>
            <p className="mb-3">
              This privacy-first approach reflects our commitment to data minimalism and transparency.
            </p>
            <p>
              For any questions, feel free to contact us at{" "}
              <a href="mailto:support@moonlytics.ai" className="text-cyan-600 hover:text-cyan-800">
                support@moonlytics.ai
              </a>.
            </p>
          </div>
        )}

      </div>
    </div>
  );
}
